/**
 * TikTokEventsAPIEditor - Configuration editor for TikTok Events API (Server-Side)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Switch,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import type { TikTokEventsAPIConfig, PlatformConfig } from '../../types/analytics.types';
import {
  getProjectAnalyticsService,
  type TikTokEventsAPITestResult,
} from '../../services/ProjectAnalyticsService';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

interface TikTokEventsAPIEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
  projectId?: string;
}

export const TikTokEventsAPIEditor: React.FC<TikTokEventsAPIEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
  projectId,
}) => {
  const ttConfig = config as TikTokEventsAPIConfig;
  const [localConfig, setLocalConfig] = useState<TikTokEventsAPIConfig>(ttConfig);
  const [localName, setLocalName] = useState(name || '');

  // Test event state
  const [isSending, setIsSending] = useState(false);
  const [testResult, setTestResult] = useState<TikTokEventsAPITestResult | null>(null);

  useEffect(() => {
    setLocalConfig(ttConfig);
    setLocalName(name || '');
  }, [ttConfig, name]);

  const handleChange = useCallback(
    (field: keyof TikTokEventsAPIConfig, value: unknown) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(localConfig, localName);
    }
  }, [onSave, localConfig, localName]);

  const handleSendTestEvent = useCallback(async () => {
    if (!projectId || !localConfig.pixelCode || !localConfig.accessToken) {
      setTestResult({
        success: false,
        error: 'Missing required fields: Pixel Code and Access Token',
      });
      return;
    }

    setIsSending(true);
    setTestResult(null);

    try {
      const service = getProjectAnalyticsService();
      const result = await service.sendTikTokEventsAPITestEvent({
        projectId,
        pixelCode: localConfig.pixelCode,
        accessToken: localConfig.accessToken,
        testEventCode: localConfig.testEventCode,
        eventName: 'Test Segmently Integration',
      });
      setTestResult(result);
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsSending(false);
    }
  }, [projectId, localConfig.pixelCode, localConfig.accessToken, localConfig.testEventCode]);

  const canSendTest = Boolean(
    projectId &&
    localConfig.pixelCode &&
    localConfig.accessToken &&
    !disabled &&
    !readOnly &&
    !isSending
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        TikTok Events API sends purchase events server-side for better attribution accuracy.
        This works alongside the client-side TikTok Pixel for event deduplication.
      </Alert>

      <TextField
        label="Pixel Code"
        value={localConfig.pixelCode || ''}
        onChange={(e) => handleChange('pixelCode', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        placeholder="CXXXXXXXXXXXXXXXXX"
        helperText={
          <>
            Same Pixel Code as your client-side TikTok Pixel. Find it in{' '}
            <Link
              href="https://ads.tiktok.com/help/article/tiktok-pixel"
              target="_blank"
              rel="noopener"
            >
              TikTok Ads Manager
            </Link>
          </>
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('tiktok_events_api', 'pixelCode') } }}
      />

      <TextField
        label="Access Token"
        value={localConfig.accessToken || ''}
        onChange={(e) => handleChange('accessToken', e.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        type="password"
        sx={{ mb: 2 }}
        placeholder="Your TikTok Business Center access token"
        helperText={
          <>
            Generate an access token in{' '}
            <Link
              href="https://business-api.tiktok.com/portal/docs"
              target="_blank"
              rel="noopener"
            >
              TikTok Business Center
            </Link>
            {' with Pixel tracking permissions.'}
          </>
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('tiktok_events_api', 'accessToken') } }}
      />

      {/* Test Event Code with Send Test button */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <TextField
          label="Test Event Code (optional)"
          value={localConfig.testEventCode || ''}
          onChange={(e) => handleChange('testEventCode', e.target.value)}
          disabled={disabled || readOnly}
          fullWidth
          placeholder="TEST12345"
          helperText="Optional code for verifying events in TikTok Events Manager → Test Events"
        />
        <Button
          variant="outlined"
          onClick={handleSendTestEvent}
          disabled={!canSendTest}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{ mt: 1, minWidth: 130, whiteSpace: 'nowrap' }}
        >
          {isSending ? 'Sending...' : 'Send Test'}
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={localConfig.renewalsAsPurchase ?? true}
              onChange={(e) => handleChange('renewalsAsPurchase', e.target.checked)}
              disabled={disabled || readOnly}
            />
          }
          label="Send subscription renewals as CompletePayment"
        />
        <FormHelperText sx={{ ml: 0 }}>
          ON: renewals count as CompletePayment (inflates payment/ROAS but feeds value optimization).
          OFF: renewals arrive as custom &quot;RecurringPayment&quot; event (same user matching).
        </FormHelperText>
      </Box>

      {/* Test Result Alert */}
      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          sx={{ mb: 2 }}
          onClose={() => setTestResult(null)}
        >
          {testResult.success
            ? 'Test event sent successfully! Check TikTok Events Manager.'
            : `Error: ${testResult.error}`}
        </Alert>
      )}

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.pixelCode || !localConfig.accessToken}
            data-testid={ANALYTICS_SETTINGS_TEST_IDS.editorSave('tiktok_events_api')}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
